import { Injectable } from '@angular/core';
import Anthropic from '@anthropic-ai/sdk';
import { ImisApiService, MemberQueryParams, ContactLookupParams } from './imis-api.service';
import { environment } from '../../../environments/environment';
import { firstValueFrom, of, catchError } from 'rxjs';

export interface ChatMessage {
  role: 'user' | 'assistant' | 'system';
  content: string;
  html?: string;
  timestamp?: Date;
}

@Injectable({
  providedIn: 'root'
})
export class ChatbotService {
  private anthropic: Anthropic | null = null;
  private conversationHistory: Anthropic.MessageParam[] = [];
  public lastQueryResults: any[] = [];
  private lastQueryHtml = '';
  private _gridUpdatedThisTurn = false;
  public contextMemberId: string | null = null;
  private apiKeyInitPromise: Promise<void> | null = null;

  private tools: Anthropic.Tool[] = [
    {
      name: 'query_members',
      description: 'Query members from iMIS database with various filters. Use this for any member search or listing request.',
      input_schema: {
        type: 'object',
        properties: {
          firstName: {
            type: 'string',
            description: 'Filter by member\'s first name'
          },
          company: {
            type: 'string',
            description: 'Filter by company name (e.g., "RMIT Melbourne")'
          },
          lastName: {
            type: 'string',
            description: 'Filter by member\'s last name'
          },
          gender: {
            type: 'string',
            enum: ['M', 'F'],
            description: 'Filter by gender: "M" for male, "F" for female'
          },
          financialStatus: {
            type: 'string',
            enum: ['financial', 'unfinancial', 'non_fee_paying'],
            description: 'Filter by membership financial status'
          },
          status: {
            type: 'string',
            enum: ['active'],
            description: 'Filter by member status'
          },
          isMember: {
            type: 'boolean',
            description: 'Filter to include only members (true) or all contacts'
          }
        },
        required: []
      }
    },
    {
      name: 'lookup_contact',
      description: 'Look up a contact/member by name to retrieve their iMIS ID. Use this before add_interaction when you only have a name. If multiple contacts are returned, present them to the user and ask which one to use.',
      input_schema: {
        type: 'object',
        properties: {
          firstName: {
            type: 'string',
            description: 'Contact first name (or partial first name)'
          },
          lastName: {
            type: 'string',
            description: 'Contact last name (or partial last name)'
          }
        },
        required: []
      }
    },
    {
      name: 'lookup_worksite',
      description: 'Search for a worksite/organisation by name to find its iMIS ID. Always use this before record_site_visit when you only have the worksite name.',
      input_schema: {
        type: 'object',
        properties: {
          name: {
            type: 'string',
            description: 'Name or partial name of the worksite/organisation (e.g., "RMIT Melbourne")'
          }
        },
        required: ['name']
      }
    },
    {
      name: 'record_site_visit',
      description: 'Record a site visit/check-in for a worksite. Always call lookup_worksite first to get the worksite ID. VisitType and LeadStaff are set automatically — do not ask the user for them.',
      input_schema: {
        type: 'object',
        properties: {
          worksiteId: {
            type: 'string',
            description: 'The iMIS ID of the worksite (from lookup_worksite result)'
          },
          date: {
            type: 'string',
            description: "The date the user mentioned for this visit. Pass the user's exact wording verbatim (e.g. '25/04/2026', 'yesterday'). Omit if no date was mentioned so it defaults to today."
          }
        },
        required: ['worksiteId']
      }
    },
    {
      name: 'add_interaction',
      description: 'Add a note/interaction to a member record in iMIS. If you only have the member name and not their ID, first call query_members to find them, then use the returned ID here.',
      input_schema: {
        type: 'object',
        properties: {
          memberId: {
            type: 'string',
            description: 'The iMIS member ID to add the note to'
          },
          note: {
            type: 'string',
            description: 'The note text to record on the member'
          },
          date: {
            type: 'string',
            description: "The date the user mentioned for this interaction. Pass the user's exact wording verbatim (e.g. '18/04/2026', 'April 18', 'yesterday'). Do NOT convert or modify it. Omit only if the user did not mention any date."
          },
          type: {
            type: 'string',
            enum: ['Note', 'Email', 'Phone', 'Meeting'],
            description: 'Interaction type. Defaults to "Note".'
          }
        },
        required: ['memberId', 'note']
      }
    }
  ];

  constructor(private imisApi: ImisApiService) {}

  private ensureAnthropicClient(): Promise<void> {
    if (this.anthropic) return Promise.resolve();
    if (this.apiKeyInitPromise) return this.apiKeyInitPromise;

    this.apiKeyInitPromise = firstValueFrom(
      this.imisApi.fetchApiKey().pipe(
        catchError(err => {
          console.warn('⚠️ Could not fetch API key from iMIS — falling back to environment config. Reason:', err?.message ?? err);
          return of(environment.anthropic.apiKey);
        })
      )
    ).then(key => {
      this.anthropic = new Anthropic({ apiKey: key, dangerouslyAllowBrowser: true });
    }).catch(err => {
      this.apiKeyInitPromise = null; // allow retry on next chat call
      throw err;
    });

    return this.apiKeyInitPromise;
  }

  private get systemPrompt(): string {
    const now = new Date();
    const todayStr = now.toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' });
    let prompt = this._systemPromptTemplate.replace('{{TODAY}}', todayStr);
    if (this.contextMemberId) {
      prompt += `\n\nPage context: The user is currently viewing member ID ${this.contextMemberId}. For any "Add note" request where the user does not explicitly mention a different member, skip asking for the member name and skip calling lookup_contact — call add_interaction directly using member ID ${this.contextMemberId}.`;
    }
    return prompt;
  }

  private readonly _systemPromptTemplate = `You are a helpful assistant for querying and updating the iMIS membership database.

Today's date is {{TODAY}}.

For member searches, use query_members.
Common mappings:
- "female members" -> gender: "F"
- "male members" -> gender: "M"
- "unfinancial members" -> financialStatus: "unfinancial"
- "financial members" -> financialStatus: "financial"
- "active members" -> status: "active"
- "members" or "list members" -> isMember: true
- "non-members" -> isMember: false
- Company, first names, and last names should be used as-is.

For recording a site visit/check-in:
- If the user has not provided a workplace/worksite name, ask: "Which workplace is this check-in for?"
- If the user has not provided a date, ask: "What is the date of the check-in? (Leave blank to use today)"
- Gather both pieces of information through conversation before calling any tools. Do not call lookup_worksite until you have the workplace name.
- Once you have the workplace name, call lookup_worksite to find its iMIS ID.
- If lookup_worksite returns exactly one result, proceed directly to record_site_visit using that ID.
- If lookup_worksite returns multiple results, list them clearly and ask the user to confirm which one before calling record_site_visit.
- For the date: if the user mentions a date, pass it verbatim. If no date is mentioned, omit the date parameter so it defaults to today.
- VisitType ("SV") and LeadStaff are set automatically — never ask the user for these.

For adding notes/interactions to a member:
- If the user has not provided a member name, ask: "Which member is this note for?"
- If the user has not provided a date, ask: "What date is this note for? If no date is provided, the note will be added for today, {{TODAY}}."
- If the user has not provided the note text, ask: "What would you like the note to say?"
- Gather all three pieces of information through conversation before calling any tools. Do not call lookup_contact until you have the member name.
- Once you have the member name, call lookup_contact to find their iMIS ID.
- If lookup_contact returns exactly one result, proceed directly to add_interaction using that ID. In your response confirming the member, include their ID and a clickable link using HTML: <a href="PROFILE_URL" target="_blank">View in iMIS</a> — the profile URL is included in the tool result.
- If lookup_contact returns multiple results, list them clearly (name + ID + clickable link) and ask the user to confirm which one before calling add_interaction.
- If lookup_contact returns no results, tell the user no matching contact was found.
- Default type to "Note" unless the user specifies Phone, Email, or Meeting.
- For the date: if the user mentions a date in any format (e.g. "18/04/2026", "April 18", "last Monday"), pass it verbatim as the date parameter — do not convert it. If no date is mentioned, omit the date parameter entirely so it defaults to today.

Always be helpful and provide clear results.`;

  private sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  private isOverloaded(error: any): boolean {
    return error?.status === 529 || error?.error?.type === 'overloaded_error';
  }

  /**
   * Process a user message and return a response
   */
  async chat(userMessage: string): Promise<ChatMessage> {
    this.conversationHistory.push({ role: 'user', content: userMessage });
    this._gridUpdatedThisTurn = false;

    const MAX_RETRIES = 3;

    for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
      // Snapshot history after user message so retries don't accumulate partial tool calls
      const historyCheckpoint = [...this.conversationHistory];

      try {
        await this.ensureAnthropicClient();

        // Agentic loop: keep calling until Claude produces a text response
        for (let i = 0; i < 5; i++) {
          const response = await this.anthropic!.messages.create({
            model: environment.anthropic.model,
            max_tokens: 4096,
            system: this.systemPrompt,
            messages: this.conversationHistory,
            tools: this.tools,
          });

          if (response.stop_reason === 'tool_use') {
            // Append full assistant turn (includes tool_use blocks) to history
            this.conversationHistory.push({ role: 'assistant', content: response.content });

            // Collect all tool results — Anthropic requires them in a single user message
            const toolResults: Anthropic.ToolResultBlockParam[] = [];
            for (const block of response.content) {
              if (block.type === 'tool_use') {
                console.log(`Calling tool: ${block.name}`, block.input);
                const result = await this.executeFunction(block.name, block.input as any);
                toolResults.push({
                  type: 'tool_result',
                  tool_use_id: block.id,
                  content: result
                });
              }
            }
            this.conversationHistory.push({ role: 'user', content: toolResults });
          } else {
            // end_turn — extract the text response
            const textBlock = response.content.find((b: Anthropic.ContentBlock): b is Anthropic.TextBlock => b.type === 'text');
            const assistantMessage = textBlock?.text || 'No response';
            this.conversationHistory.push({ role: 'assistant', content: assistantMessage });
            const msg: ChatMessage = { role: 'assistant', content: assistantMessage, timestamp: new Date() };
            if (this._gridUpdatedThisTurn) msg.html = this.lastQueryHtml;
            return msg;
          }
        }

        const fallback = 'Maximum tool calls reached without a final response.';
        this.conversationHistory.push({ role: 'assistant', content: fallback });
        return { role: 'assistant', content: fallback, timestamp: new Date() };

      } catch (error: any) {
        if (this.isOverloaded(error) && attempt < MAX_RETRIES) {
          this.conversationHistory = historyCheckpoint;
          const delay = 2000 * Math.pow(2, attempt); // 2s, 4s, 8s
          console.warn(`Anthropic overloaded (attempt ${attempt + 1}/${MAX_RETRIES}), retrying in ${delay}ms…`);
          await this.sleep(delay);
          continue;
        }

        const errorMessage = this.isOverloaded(error)
          ? 'The assistant is currently at capacity. Please wait a moment and try again.'
          : `Error processing request: ${error.message}`;
        this.conversationHistory.push({ role: 'assistant', content: errorMessage });
        return { role: 'assistant', content: errorMessage, timestamp: new Date() };
      }
    }

    const finalFallback = 'The assistant is currently at capacity. Please wait a moment and try again.';
    return { role: 'assistant', content: finalFallback, timestamp: new Date() };
  }

  /**
   * Execute a tool call from Claude
   */
  private async executeFunction(functionName: string, args: any): Promise<string> {
    if (functionName === 'query_members') {
      try {
        console.log('🔧 Function args from Claude:', args);

        const params: MemberQueryParams = {
          firstName: args.firstName,
          company: args.company,
          lastName: args.lastName,
          gender: args.gender,
          financialStatus: args.financialStatus,
          status: args.status,
          isMember: args.isMember
        };

        console.log('📤 Passing params to API:', params);

        const result = await firstValueFrom(this.imisApi.queryMembers(params));

        if (result.success) {
          const members = result.members;
          const returned = result.count;
          const total = result.totalCount ?? returned;

          this.lastQueryResults = members;

          if (returned === 0) {
            return 'No members found matching the criteria.';
          }

          this.lastQueryHtml = this.buildMemberGrid(members);
          this._gridUpdatedThisTurn = true;

          let response = `Found ${total} member(s)`;
          if (returned < total) {
            response += ` (showing first ${returned} due to iMIS query limit)`;
          }
          response += `. Results are displayed in the grid below.`;
          if (returned > 0) {
            response += ` Use the Export CSV button to download all records.`;
          }
          return response;
        } else {
          return `Error querying members: ${result.error || 'Unknown error'}`;
        }
      } catch (error: any) {
        return `Error executing query: ${error.message}`;
      }
    }

    if (functionName === 'lookup_worksite') {
      try {
        console.log('🏢 lookup_worksite args:', args);
        const result = await firstValueFrom(this.imisApi.lookupWorksite(args.name));

        if (!result.success || result.count === 0) {
          return `No worksites found matching "${args.name}".`;
        }

        const format = (w: any) => {
          const parts = Object.entries(w)
            .filter(([k]) => k !== 'ID')
            .map(([, v]) => v)
            .filter(Boolean)
            .join(' — ');
          return `${parts} — ID: ${w.ID}`;
        };

        if (result.count === 1) {
          return `Found 1 worksite: ${format(result.members[0])}`;
        }

        let response = `Found ${result.count} worksites matching "${args.name}". Please confirm which one:\n\n`;
        result.members.forEach((w, i) => { response += `${i + 1}. ${format(w)}\n`; });
        return response;
      } catch (error: any) {
        return `Error looking up worksite: ${error.message ?? error.error ?? 'Unknown error'}`;
      }
    }

    if (functionName === 'record_site_visit') {
      try {
        console.log('📍 record_site_visit args:', args);

        const resolvedDate = this.normalizeDate(args.date)
          ?? new Date().toLocaleDateString('en-CA') + 'T00:00:00';
        console.log('📅 Site visit date:', args.date ? `from user: "${args.date}" → ${resolvedDate}` : `defaulting to today: ${resolvedDate}`);

        const result = await firstValueFrom(
          this.imisApi.addSiteVisit({ worksiteId: args.worksiteId, date: resolvedDate })
        );
        return result.message;
      } catch (error: any) {
        return `Error recording site visit: ${error.message ?? error.error ?? 'Unknown error'}`;
      }
    }

    if (functionName === 'lookup_contact') {
      try {
        console.log('🔎 lookup_contact args:', args);

        const params: ContactLookupParams = {
          firstName: args.firstName,
          lastName: args.lastName
        };

        const result = await firstValueFrom(this.imisApi.lookupContact(params));

        if (!result.success || result.count === 0) {
          return 'No contacts found matching that name.';
        }

        const format = (c: any) => {
          let line = c['FULL_NAME'] || 'Unknown';
          if (c['Worksite']) line += ` — ${c['Worksite']}`;
          if (c['Member Type']) line += ` (${c['Member Type']})`;
          line += ` — ID: ${c['ID']}`;
          return line;
        };

        const profileUrl = (id: string) =>
          `/_i4u_/Core/Staff-Site-Layouts/Contact-Layouts/Individual/Account_Page_Staff.aspx?ID=${encodeURIComponent(id)}`;

        if (result.count === 1) {
          const c = result.members[0];
          const url = c['ID'] ? profileUrl(c['ID']) : null;
          let line = `Found 1 contact: ${format(c)}`;
          if (url) line += `. iMIS profile URL: ${url}`;
          return line;
        }

        let response = `Found ${result.count} contacts with that name. Please confirm which one:\n\n`;
        result.members.forEach((c, i) => {
          const url = c['ID'] ? profileUrl(c['ID']) : null;
          response += `${i + 1}. ${format(c)}${url ? ` — Profile: ${url}` : ''}\n`;
        });
        return response;
      } catch (error: any) {
        return `Error looking up contact: ${error.message ?? error.error ?? 'Unknown error'}`;
      }
    }

    if (functionName === 'add_interaction') {
      try {
        console.log('📝 add_interaction args:', args);

        const resolvedDate = this.normalizeDate(args.date)
          ?? new Date().toLocaleDateString('en-CA') + 'T00:00:00'; // en-CA gives YYYY-MM-DD
        console.log('📅 Interaction date:', args.date ? `from user: "${args.date}" → ${resolvedDate}` : `defaulting to today: ${resolvedDate}`);

        const result = await firstValueFrom(
          this.imisApi.addInteraction({
            memberId: args.memberId,
            note: args.note,
            date: resolvedDate,
            type: args.type
          })
        );

        return result.message;
      } catch (error: any) {
        return `Error adding note: ${error.message ?? error.error ?? 'Unknown error'}`;
      }
    }

    return `Unknown function: ${functionName}`;
  }

  private buildMemberGrid(members: any[]): string {
    const cols: { key: string; label: string }[] = [
      { key: 'FullName',   label: 'Name' },
      { key: 'FirstName',  label: 'First Name' },
      { key: 'Company',    label: 'Company' },
      { key: 'MemberType', label: 'Type' },
      { key: 'Chapter',    label: 'Chapter' },
    ];
    const activeCols = cols.filter(c => members.some(m => m[c.key] != null && m[c.key] !== ''));
    const esc = (s: any) => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

    const headerCells = [...activeCols.map(c => `<th>${c.label}</th>`), '<th>ID</th>'].join('');
    const bodyRows = members.map(m => {
      const cells = activeCols.map(c => `<td>${esc(m[c.key])}</td>`).join('');
      const idCell = m.ID
        ? `<td><a href="/_i4u_/Core/Staff-Site-Layouts/Contact-Layouts/Individual/Account_Page_Staff.aspx?ID=${esc(m.ID)}" target="_blank">${esc(m.ID)}</a></td>`
        : '<td></td>';
      return `<tr>${cells}${idCell}</tr>`;
    }).join('');

    return `<div class="member-grid-wrapper"><table class="member-grid-table"><thead><tr>${headerCells}</tr></thead><tbody>${bodyRows}</tbody></table></div>`;
  }

  private normalizeDate(dateStr: string | undefined): string | undefined {
    if (!dateStr) return undefined;

    // Already ISO format (YYYY-MM-DD...)
    if (/^\d{4}-\d{2}-\d{2}/.test(dateStr)) return dateStr;

    // DD/MM/YYYY (Australian/European)
    const ddmmyyyy = dateStr.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (ddmmyyyy) {
      return `${ddmmyyyy[3]}-${ddmmyyyy[2].padStart(2, '0')}-${ddmmyyyy[1].padStart(2, '0')}T00:00:00`;
    }

    // MM/DD/YYYY (US)
    const mmddyyyy = dateStr.match(/^(\d{1,2})-(\d{1,2})-(\d{4})$/);
    if (mmddyyyy) {
      return `${mmddyyyy[3]}-${mmddyyyy[1].padStart(2, '0')}-${mmddyyyy[2].padStart(2, '0')}T00:00:00`;
    }

    // Fallback: let Date parse it (handles "April 18 2026", "18 April 2026", etc.)
    // Use local date parts (not toISOString) to avoid UTC timezone shift for users in UTC+ zones
    const parsed = new Date(dateStr);
    if (!isNaN(parsed.getTime())) {
      const y = parsed.getFullYear();
      const m = String(parsed.getMonth() + 1).padStart(2, '0');
      const d = String(parsed.getDate()).padStart(2, '0');
      return `${y}-${m}-${d}T00:00:00`;
    }

    console.warn('⚠️ Could not parse date:', dateStr);
    return undefined;
  }

  /**
   * Reset the conversation history
   */
  resetConversation(): void {
    this.conversationHistory = [];
    this.lastQueryHtml = '';
    this._gridUpdatedThisTurn = false;
  }

  /**
   * Get the conversation history
   */
  getHistory(): any[] {
    return this.conversationHistory;
  }

  /**
   * Export results as CSV
   */
  exportAsCSV(): void {
    if (this.lastQueryResults.length === 0) {
      alert('No results to export');
      return;
    }

    // Get all unique keys from all objects
    const allKeys = new Set<string>();
    this.lastQueryResults.forEach(obj => {
      Object.keys(obj).forEach(key => allKeys.add(key));
    });
    const headers = Array.from(allKeys);

    // Create CSV content
    let csv = headers.join(',') + '\n';
    this.lastQueryResults.forEach(row => {
      const values = headers.map(header => {
        const value = row[header] || '';
        // Escape commas and quotes
        return `"${String(value).replace(/"/g, '""')}"`;
      });
      csv += values.join(',') + '\n';
    });

    // Download file
    this.downloadFile(csv, 'imis-query-results.csv', 'text/csv');
  }

  /**
   * Helper method to download a file
   */
  private downloadFile(content: string, filename: string, mimeType: string): void {
    const blob = new Blob([content], { type: mimeType });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }
}
